import React, { useState, useEffect } from 'react';
import { UserCheck, Filter, Clock, Archive, Download, ArrowLeftRight, Users, ShoppingBag, Tag, History, Trash2, LogOut, DoorOpen, ChevronRight, X } from 'lucide-react';
import { F, Icon, haptic, Z } from '../theme.jsx';
import {
  useModalViewport, modalShellStyle, ModalHeader,
} from '../modal-utils.jsx';
import { supabase } from '../supabase.js';
import {
  ConfirmDeleteStoreSheet,
} from '../store-sheets.jsx';
import {
  SectionLabel as SL, SettingsGroup as SG, SettingsRow as SR, SettingsValueRow as SVR,
  SettingsToggleRow as STR, SettingsSegmentedRow as SSR, SettingsDivider as SD,
} from '../components/boothly/settings-ui.jsx';
import { useToast } from '../components/boothly/toast-provider.jsx';
import { useConfirm } from '../components/boothly/confirm-provider.jsx';
import { fmtCurrency, fmtDecimal } from '../lib/format.js';

// Default status labels — keep in sync with use-online-store.js
export const DEFAULT_STATUS_LABELS = { Offen: "Offen", Bereit: "Bereit", Versendet: "Versendet", Retoure: "Retoure" };

// ─────────────────────────────────────────────────────────────
// NewChannelModal — Anlegen eines neuen Kanals (Verkauf oder Reservierung).
// ─────────────────────────────────────────────────────────────
// Online-Store Umbau Phase 3: Kanäle leben in der channels-Tabelle. Felder sind
// nur Name + Gebühr % + Versand €. Ein-Tap-Presets befüllen Name + typische Gebühr
// (editierbar). onCreate(name, { fee_percent, shipping_cost }).
const CHANNEL_PRESETS = [
  { label: "Direktverkauf", fee_percent: 0,    shipping_cost: 0 },
  { label: "eBay",          fee_percent: 10,   shipping_cost: 0 },
  { label: "Etsy",          fee_percent: 6.5,  shipping_cost: 0 },
];

export function NewChannelModal({ t, dark, F: _F, glass, onClose, onCreate }) {
  const Fam = _F || F;
  const [name, setName] = useState("");
  const [feePercent, setFeePercent] = useState("0");
  const [shipping, setShipping] = useState("0");
  const [submitting, setSubmitting] = useState(false);
  const trimmed = name.trim();
  const { height: vh, kbOpen, offsetTop } = useModalViewport();

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, []);

  const applyPreset = (p) => {
    setName(p.label);
    setFeePercent(String(p.fee_percent));
    setShipping(String(p.shipping_cost));
  };

  const submit = async () => {
    if (!trimmed || submitting) return;
    const fee = parseFloat(String(feePercent).replace(",", ".")) || 0;
    const ship = parseFloat(String(shipping).replace(",", ".")) || 0;
    setSubmitting(true);
    await onCreate(trimmed, {
      fee_percent: Math.min(100, Math.max(0, fee)),
      shipping_cost: Math.max(0, ship),
    });
  };

  const numField = (label, suffix, value, setValue) => (
    <div style={{ ...glass({ borderRadius: 12 }), padding: "8px 12px" }}>
      <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, marginBottom: 2 }}>{label} {suffix}</div>
      <input
        type="number" step="0.01" min="0"
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && trimmed) submit(); }}
        style={{ width: "100%", border: "none", background: "transparent", fontFamily: Fam, fontSize: 15, fontWeight: 600, color: t.text, outline: "none", padding: 0 }}
      />
    </div>
  );

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
        touchAction: "none",
        willChange: "opacity",
      }}
        onTouchMove={e => e.preventDefault()}
      />
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, glass, F: Fam, dark, width: 400, radius: 22, zIndex: 93 })}>
        <ModalHeader t={t} dark={dark} F={Fam} title="Neuer Kanal" onClose={onClose} />

        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
          Vorlagen
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {CHANNEL_PRESETS.map(p => (
            <button key={p.label} onClick={() => applyPreset(p)} style={{
              padding: "8px 14px", borderRadius: 9999, cursor: "pointer", fontFamily: Fam,
              background: name === p.label ? t.accent.blue.weak : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
              border: `1px solid ${name === p.label ? t.accent.blue.solid : t.glassBorder}`,
              color: name === p.label ? t.accent.blue.solid : t.text,
              fontSize: 13, fontWeight: 600, transition: "all 0.18s ease",
            }}>{p.label}</button>
          ))}
        </div>

        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
          Name
        </div>
        <div style={{ ...glass({ borderRadius: 12 }), padding: "0 14px", display: "flex", alignItems: "center" }}>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && trimmed) submit(); }}
            placeholder="z.B. eBay, Etsy, Direktverkauf …"
            style={{
              flex: 1, background: "transparent", border: "none",
              fontFamily: Fam, fontSize: 15, color: t.text, outline: "none",
              padding: "12px 0", letterSpacing: "-0.2px",
            }}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }}>
          {numField("Gebühr", "%", feePercent, setFeePercent)}
          {numField("Versand", "€", shipping, setShipping)}
        </div>

        <div style={{ fontSize: 11, color: t.textSub, marginTop: 8, padding: "0 4px", lineHeight: 1.4, fontFamily: Fam }}>
          Bestellungen werden über diesen Kanal erfasst. Der Verkaufspreis lebt am Produkt im Katalog;
          Gebühr &amp; Versand gelten pro Kanal und werden auf jede Bestellung gesnapshottet.
        </div>

        <button
          onClick={submit}
          disabled={!trimmed || submitting}
          onMouseDown={e => e.preventDefault()}
          onTouchStart={e => e.preventDefault()}
          style={{
            width: "100%", marginTop: 22,
            background: trimmed ? t.accent.blue.solid : t.btnGhost,
            color: trimmed ? "#fff" : t.textMuted,
            border: "none", borderRadius: 14, padding: "14px",
            fontFamily: Fam, fontWeight: 700, fontSize: 15, letterSpacing: "-0.1px",
            cursor: trimmed && !submitting ? "pointer" : "not-allowed",
            boxShadow: trimmed && !submitting ? `0 4px 18px ${t.accent.blue.weak}` : "none",
            transition: "all 0.18s ease",
          }}
        >Anlegen</button>
      </div>
    </>
  );
}


// Legacy — nicht mehr intern genutzt, aber exportiert (könnte extern referenziert werden).
// Neues Design: SectionLabel + SettingsGroup aus components/boothly/settings-ui.jsx
export function SettingsSection({ title, t, children }) {
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: t.textMuted,
        letterSpacing: "0.04em", textTransform: "uppercase",
        marginBottom: 8, paddingLeft: 32,
      }}>{title}</div>
      <div style={{
        background: t.glass,
        backdropFilter: "blur(40px) saturate(200%)",
        WebkitBackdropFilter: "blur(40px) saturate(200%)",
        border: `1px solid ${t.glassBorder}`,
        boxShadow: t.glassShadow,
        borderRadius: 14, overflow: "hidden",
        marginLeft: 16, marginRight: 16,
      }}>{children}</div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// SettingsSegmentedRow — Row mit Segmented-Control rechts.
// ─────────────────────────────────────────────────────────────
export function SettingsSegmentedRow({ icon, accent, label, sub, options, value, onChange, t, dark, F: _F, first }) {
  const Fam = _F || F;
  return (
    <div style={{
      padding: "12px 16px",
      display: "flex", alignItems: "center", gap: 10,
      borderTop: first ? "none" : `0.5px solid ${t.divider}`,
      fontFamily: Fam,
    }}>
      {icon && (
        <span style={{
          color: accent || t.textMuted,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>{typeof icon === "function" ? icon(16) : icon}</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: t.text, fontWeight: 500, letterSpacing: "-0.1px" }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, lineHeight: 1.3 }}>{sub}</div>}
      </div>
      <div style={{
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
        borderRadius: 8, padding: 2,
        display: "flex", flexShrink: 0,
      }}>
        {options.map(opt => (
          <button key={opt.v} onClick={(e) => { e.stopPropagation(); onChange(opt.v); }} style={{
            background: value === opt.v ? t.pillBg : "transparent",
            border: "none", borderRadius: 6,
            padding: "5px 10px", cursor: "pointer", fontFamily: Fam,
            fontSize: 12, fontWeight: value === opt.v ? 600 : 500,
            color: value === opt.v ? t.text : t.textSub,
            boxShadow: value === opt.v ? `0 1px 4px rgba(0,0,0,${dark?0.3:0.08})` : "none",
            transition: "all 0.15s ease",
          }}>{opt.l}</button>
        ))}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// KanalPickerModal — kleines Modal-Sheet mit Channel-Liste + Checkmarks.
// ─────────────────────────────────────────────────────────────
export function KanalPickerModal({ t, dark, F: _F, glass, channels, value, onPick, onClose }) {
  const Fam = _F || F;
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
        willChange: "opacity",
      }} />
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, glass, F: Fam, dark, width: 360, radius: 24, padding: "20px 22px 12px", zIndex: 61 })}>
        <ModalHeader t={t} dark={dark} F={Fam} title="Kanal-Filter" onClose={onClose} marginBottom={12} />
        <div style={{ margin: "0 -10px" }}>
          {channels.map((ch, i) => (
            <button key={ch.id} onClick={() => { onPick(ch.id); onClose(); }} style={{
              width: "100%", background: "none", border: "none", cursor: "pointer",
              padding: "13px 12px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              fontFamily: Fam, textAlign: "left",
              borderTop: i > 0 ? `0.5px solid ${t.divider}` : "none",
            }}>
              <span style={{ fontSize: 15, color: t.text, fontWeight: 400 }}>{ch.name}</span>
              {value === ch.id && (
                <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
                  <path d="M1 6L5.5 10.5L15 1" stroke={t.accent.blue.solid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}


// ─────────────────────────────────────────────────────────────
// StoreSettingsGroup — renders all settings/channels/members/audit modals.
// Extracted from OnlineStore to keep online-store.jsx under LoC target.
// ─────────────────────────────────────────────────────────────
export function StoreSettingsGroup({
  // layout / theme
  dark, t, glass, appVh, appKbOpen, appOffsetTop,
  // store context
  currentStore, currentRole, authToken, authUser,
  // data
  fmt2, orders, archive, userStores, onBack,
  // channels state (Phase 3: channels-Tabelle statt Legacy-JSONB)
  channels, storeStatusLabels, setStoreStatusLabels,
  expandedChannelId, setExpandedChannelId,
  newChannelOpen, setNewChannelOpen,
  // settings visibility
  showSettings, setShowSettings,
  showChannelsConfig, setShowChannelsConfig,
  showStatusLabelsConfig, setShowStatusLabelsConfig,
  showMembers, setShowMembers,
  showAuditLog, setShowAuditLog,
  showCustomizeDashboard, setShowCustomizeDashboard,
  showArchive, setShowArchive,
  filterPickerOpen, setFilterPickerOpen,
  confirmDeleteStoreOpen, setConfirmDeleteStoreOpen,
  // audit log state
  auditEntries, setAuditEntries,
  auditFilter, setAuditFilter,
  auditLoading, setAuditLoading,
  auditHasMore, setAuditHasMore,
  // members state
  membersList, memberLoading, invitationsList,
  memberSheetTab, setMemberSheetTab,
  newInviteRole, setNewInviteRole,
  createdInviteCode, setCreatedInviteCode,
  confirmRemoveUserId, setConfirmRemoveUserId,
  // filter/display state
  filterKanal, setFilterKanal,
  dateRange, setDateRange,
  // functions
  can, getAllChannels, getChannelFinance, slugifyChannelName,
  apiChannelCreate, apiChannelUpdate, apiChannelDelete,
  saveStoreConfig,
  loadMembersAndInvitations, createInvitation, revokeInvitation,
  changeMemberRole, removeMember,
  setDark,
  loadAuditLog, describeAuditEntry, formatRelative,
  exportCSV, destroyCurrentStore, leaveCurrentStore,
  // helfer modus (nur für owner/admin sichtbar)
  helperMode, toggleHelperMode,
}) {
  const toast = useToast();
  const confirm = useConfirm();
  // Phase 3: lokaler Delete-Confirm-State (war früher im Hook; jetzt rein UI).
  const [deleteChannelId, setDeleteChannelId] = useState(null);
  return (
    <>
      {/* ── NEW CHANNEL MODAL — channels-Tabelle via apiChannelCreate ── */}
      {newChannelOpen && (
        <NewChannelModal
          t={t} dark={dark} F={F} glass={glass}
          onClose={() => setNewChannelOpen(false)}
          onCreate={async (name, finance) => {
            const trimmed = (name || "").trim();
            if (!trimmed) return;
            const ch = await apiChannelCreate({
              name: trimmed,
              fee_percent: finance?.fee_percent ?? 0,
              shipping_cost: finance?.shipping_cost ?? 0,
            });
            if (ch) {
              toast.success(`Kanal "${trimmed}" angelegt`);
              haptic(15);
            }
            setNewChannelOpen(false);
          }}
        />
      )}

      {/* ── DELETE CHANNEL CONFIRM MODAL ── */}
      {deleteChannelId && (() => {
        const ch = (channels || []).find(c => c.id === deleteChannelId);
        if (!ch) { setDeleteChannelId(null); return null; }
        // Client-Vorabwarnung; autoritativ blockt der Worker mit 409 (in apiChannelDelete).
        const ordersUsing = orders.filter(o => o.channelId === ch.id).length
                         + archive.filter(o => o.channelId === ch.id).length;
        const blocked = ordersUsing > 0;
        return (
          <>
            <div onClick={() => setDeleteChannelId(null)} style={{
              position: "fixed", inset: 0, zIndex: Z.nestedBackdrop,
              background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
              backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
              animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
              willChange: "opacity",
            }} />
            <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 360, radius: 22, padding: "22px 22px 18px", zIndex: Z.nestedSheet })}>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text, letterSpacing: "-0.3px", marginBottom: 6 }}>
                {ch.name} löschen?
              </div>
              {blocked ? (
                <div style={{
                  fontSize: 13, color: t.accent.orange.solid,
                  background: t.accent.orange.weak,
                  border: `1px solid ${t.accent.orange.solid}33`,
                  padding: "10px 12px", borderRadius: 10,
                  marginTop: 12, lineHeight: 1.4,
                }}>
                  {`Es gibt noch ${ordersUsing} ${ordersUsing === 1 ? "Bestellung" : "Bestellungen"} mit diesem Kanal. Bestehende Bestellungen behalten ihre Finanzzahlen (Snapshot) — der Kanal lässt sich aber erst löschen, wenn keine Bestellung mehr auf ihn zeigt.`}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: t.textSub, marginTop: 6, marginBottom: 16, lineHeight: 1.4 }}>
                  Der Kanal wird entfernt. Bestehende Bestellungen behalten ihre gesnapshotteten Gebühr/Versand-Werte.
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                <button onClick={() => setDeleteChannelId(null)} style={{
                  flex: 1, ...glass({ borderRadius: 12 }),
                  border: `1px solid ${t.glassBorder}`,
                  padding: "12px", color: t.text,
                  fontFamily: F, fontWeight: 500, fontSize: 14, cursor: "pointer",
                }}>Abbrechen</button>
                <button
                  onClick={async () => {
                    const id = deleteChannelId;
                    setDeleteChannelId(null);
                    await apiChannelDelete(id);
                  }}
                  disabled={blocked}
                  style={{
                    flex: 1.2,
                    background: blocked ? t.btnGhost : t.accent.red.solid,
                    color: blocked ? t.textMuted : "#fff",
                    border: "none", borderRadius: 12, padding: "12px",
                    fontFamily: F, fontWeight: 700, fontSize: 14,
                    cursor: blocked ? "not-allowed" : "pointer",
                    boxShadow: blocked ? "none" : `0 3px 14px ${t.accent.red.weak}`,
                  }}>Löschen</button>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── ACTIVITY LOG MODAL — Owner/Admin only ── */}
      {showAuditLog && (() => {
        const ENTITY_OPTIONS = [
          { v: "all",         l: "Alle Aktionen" },
          { v: "order",       l: "Bestellungen" },
          { v: "batch",       l: "Batches" },
          { v: "member",      l: "Mitglieder" },
          { v: "invitation",  l: "Einladungen" },
          { v: "settings",    l: "Einstellungen" },
          { v: "store",       l: "Store-Konfig" },
        ];
        const DAYS_OPTIONS = [
          { v: 1,    l: "Heute" },
          { v: 7,    l: "7 Tage" },
          { v: 30,   l: "30 Tage" },
          { v: -1,   l: "Alles" },
        ];
        const setFilter = (patch) => {
          const next = { ...auditFilter, ...patch };
          setAuditFilter(next);
          setAuditEntries([]);
          setTimeout(() => {
            (async () => {
              if (!currentStore || !authToken) return;
              setAuditLoading(true);
              const params = new URLSearchParams();
              if (next.entity_type && next.entity_type !== "all") params.set("entity_type", next.entity_type);
              if (next.days && next.days > 0) params.set("from", new Date(Date.now() - next.days * 86400 * 1000).toISOString());
              params.set("limit", "50");
              const res = await fetch(`/api/stores/${currentStore.id}/audit?${params}`, {
                headers: { "Authorization": `Bearer ${authToken}` },
              });
              if (res.ok) {
                const data = await res.json();
                setAuditEntries(data.entries || []);
                setAuditHasMore(!!data.has_more);
              }
              setAuditLoading(false);
            })();
          }, 0);
        };
        return (
        <>
          <div onClick={() => setShowAuditLog(false)} style={{
            position: "fixed", inset: 0, zIndex: 80,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div onClick={e => e.stopPropagation()} style={{
            ...modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 560, radius: 24, padding: "20px 22px 22px", zIndex: 81 }),
            display: "flex", flexDirection: "column",
          }}>
            <ModalHeader
              t={t} dark={dark} F={F}
              title="Aktivitätsverlauf"
              subtitle={`${currentStore?.name || ""} — Alle Aktionen mit Zeitstempel`}
              onClose={() => setShowAuditLog(false)}
              marginBottom={14}
            />

            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <select value={auditFilter.entity_type} onChange={e => setFilter({ entity_type: e.target.value })} style={{
                flex: 1, padding: "9px 12px", fontSize: 13, fontFamily: F,
                background: t.pillActive, color: t.text, border: `1px solid ${t.glassBorder}`,
                borderRadius: 10, cursor: "pointer",
              }}>
                {ENTITY_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <select value={auditFilter.days} onChange={e => setFilter({ days: parseInt(e.target.value, 10) })} style={{
                flex: 1, padding: "9px 12px", fontSize: 13, fontFamily: F,
                background: t.pillActive, color: t.text, border: `1px solid ${t.glassBorder}`,
                borderRadius: 10, cursor: "pointer",
              }}>
                {DAYS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>

            <div style={{ flex: 1, overflowY: "auto", marginRight: -8, paddingRight: 8 }}>
              {auditLoading && auditEntries.length === 0 && (
                <div style={{ textAlign: "center", color: t.textMuted, fontSize: 13, padding: 20 }}>Lade…</div>
              )}
              {!auditLoading && auditEntries.length === 0 && (
                <div style={{ textAlign: "center", color: t.textMuted, fontSize: 13, padding: 20 }}>Keine Einträge</div>
              )}
              {auditEntries.length > 0 && (
                <div style={{ ...glass({ borderRadius: 14 }), overflow: "hidden" }}>
                  {auditEntries.map((e, i) => {
                    const desc = describeAuditEntry(e);
                    const who = e.user_email || (e.user_id ? e.user_id.slice(0, 8) : "System");
                    const isMe = e.user_id === authUser?.id;
                    return (
                      <React.Fragment key={e.id}>
                        {i > 0 && <div style={{ height: "0.5px", background: t.divider, marginLeft: 16 }} />}
                        <div style={{ padding: "11px 14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <span style={{ fontSize: 17, lineHeight: 1.2, flexShrink: 0, marginTop: 1 }}>{desc.icon}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, color: t.text, fontWeight: 500, lineHeight: 1.35 }}>
                              {desc.text}
                            </div>
                            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, display: "flex", gap: 6, flexWrap: "wrap" }}>
                              <span>{isMe ? "Du" : who}</span>
                              <span>·</span>
                              <span>{formatRelative(e.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
              {auditHasMore && (
                <button onClick={() => loadAuditLog(false)} disabled={auditLoading} style={{
                  width: "100%", marginTop: 12, padding: "10px",
                  background: t.pillActive, border: `1px solid ${t.glassBorder}`, borderRadius: 10,
                  color: "var(--accent-online)", fontFamily: F, fontSize: 13, fontWeight: 600, cursor: "pointer",
                  opacity: auditLoading ? 0.5 : 1,
                }}>{auditLoading ? "Lade…" : "Mehr laden"}</button>
              )}
            </div>
          </div>
        </>
        );
      })()}

      {/* ── CHANNELS CONFIG MODAL ── */}
      {showChannelsConfig && (() => {
        // Phase 3: Kanäle aus der channels-Tabelle (Server liefert nach created_at sortiert).
        const list = channels || [];
        const renameChannel = (id, newName) => { if (newName) apiChannelUpdate(id, { name: newName }); };
        const updateFinance = (id, field, raw) => {
          const num = parseFloat(String(raw).replace(",", "."));
          if (!Number.isFinite(num) || num < 0) return;
          if (field === "fee_percent" && num > 100) return;
          apiChannelUpdate(id, { [field]: num }); // field: "fee_percent" | "shipping_cost"
        };
        const deleteChannel = (id) => setDeleteChannelId(id);
        return (
        <>
          <div onClick={() => setShowChannelsConfig(false)} style={{
            position: "fixed", inset: 0, zIndex: 80,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 540, radius: 24, padding: "20px 22px 22px", zIndex: 81 })}>
            <ModalHeader
              t={t} dark={dark} F={F}
              title="Verkaufskanäle"
              subtitle="Gebühr und Versand gelten pro Kanal und werden auf jede Bestellung gesnapshottet. Der Verkaufspreis lebt am Produkt im Katalog."
              onClose={() => setShowChannelsConfig(false)}
              marginBottom={16}
            />

            <div style={{ ...glass({ borderRadius: 14 }), overflow: "hidden", marginBottom: 14 }}>
              {list.length === 0 && (
                <div style={{ padding: "18px 14px", fontSize: 13, color: t.textSub, textAlign: "center", lineHeight: 1.5 }}>
                  Noch kein Kanal. Lege deinen ersten Verkaufskanal an.
                </div>
              )}
              {list.map((ch, i) => {
                const isExpanded = expandedChannelId === ch.id;
                return (
                <React.Fragment key={ch.id}>
                  {i > 0 && <div style={{ height: "0.5px", background: t.divider, marginLeft: 16 }} />}
                  {/* Ganze Kachel tappbar (div, kein Button — keine nested buttons). Tap
                      öffnet/schließt den Edit-Modus. Collapsed: Werte-Vorschau + Chevron. */}
                  <div
                    id={`channel-row-${ch.id}`}
                    className="tap-target"
                    onClick={() => setExpandedChannelId(isExpanded ? null : ch.id)}
                    style={{
                      padding: "12px 14px",
                      display: "flex", gap: 10, alignItems: "center",
                      background: isExpanded ? t.accent.blue.weak : "transparent",
                      transition: "background 0.4s ease",
                      scrollMarginTop: 16,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: t.text, fontFamily: F, fontSize: 15, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ch.name}</div>
                      {!isExpanded && (
                        <div style={{ color: t.textSub, fontFamily: F, fontSize: 12, marginTop: 2 }}>
                          {fmtDecimal(ch.feePercent ?? 0, Number.isInteger(ch.feePercent ?? 0) ? 0 : 1)} % · {fmtCurrency(ch.shippingCost ?? 0)}
                        </div>
                      )}
                    </div>
                    <ChevronRight
                      size={20}
                      style={{
                        flexShrink: 0,
                        color: isExpanded ? t.accent.blue.solid : t.textMuted,
                        transform: isExpanded ? "rotate(90deg)" : "none",
                        transition: "transform 0.18s ease, color 0.18s ease",
                      }}
                    />
                  </div>
                  {isExpanded && (
                    <div style={{ padding: "0 14px 14px 18px", background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)" }}>
                      {/* Name-Edit lebt jetzt im Edit-Body (collapsed = nur Vorschau). */}
                      <div style={{ marginTop: 10, marginBottom: 8 }}>
                        <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, marginBottom: 2 }}>Name</div>
                        <input
                          defaultValue={ch.name}
                          onBlur={e => { if (e.target.value.trim() && e.target.value.trim() !== ch.name) renameChannel(ch.id, e.target.value.trim()); }}
                          onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                          style={{ width: "100%", background: "transparent", border: "none", color: t.text, fontFamily: F, fontSize: 15, fontWeight: 500, outline: "none", padding: "6px 4px", borderBottom: `1px solid ${t.glassBorder}` }}
                        />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                        {[
                          { key: "fee_percent",   label: "Gebühr",  suffix: "%", value: ch.feePercent },
                          { key: "shipping_cost", label: "Versand", suffix: "€", value: ch.shippingCost },
                        ].map(field => (
                          <div key={field.key} style={{ ...glass({ borderRadius: 10 }), padding: "8px 12px" }}>
                            <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, marginBottom: 2 }}>{field.label} {field.suffix}</div>
                            <input
                              type="number" step="0.01" min="0"
                              defaultValue={field.value ?? 0}
                              onBlur={e => updateFinance(ch.id, field.key, e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                              style={{ width: "100%", border: "none", background: "transparent", fontFamily: F, fontSize: 15, fontWeight: 600, color: t.text, outline: "none", padding: 0 }}
                            />
                          </div>
                        ))}
                      </div>
                      <button onClick={() => deleteChannel(ch.id)} style={{
                        marginTop: 4, width: "100%",
                        background: t.accent.red.weak, border: `1px solid ${t.accent.red.solid}33`,
                        borderRadius: 10, padding: "8px 12px",
                        color: t.accent.red.solid, fontSize: 12, fontWeight: 600, fontFamily: F,
                        cursor: "pointer",
                      }}>Kanal löschen</button>
                    </div>
                  )}
                </React.Fragment>
                );
              })}
            </div>

            <button onClick={() => setNewChannelOpen(true)} style={{
              width: "100%", padding: "12px", background: t.pillActive, border: `1px solid ${t.glassBorder}`,
              borderRadius: 12, color: "var(--accent-online)", fontSize: 14, fontWeight: 600, fontFamily: F, cursor: "pointer",
            }}>+ Neuen Kanal hinzufügen</button>
          </div>
        </>
        );
      })()}

      {/* ── STATUS LABELS CONFIG MODAL ── */}
      {showStatusLabelsConfig && (() => {
        const STATUS_KEYS = ["Offen", "Bereit", "Versendet", "Retoure"];
        const updateLabel = (key, val) => {
          const next = { ...storeStatusLabels, [key]: (val || "").trim() || key };
          setStoreStatusLabels(next);
          saveStoreConfig({ status_labels: next });
        };
        const resetDefaults = () => {
          setStoreStatusLabels(DEFAULT_STATUS_LABELS);
          saveStoreConfig({ status_labels: DEFAULT_STATUS_LABELS });
        };
        return (
        <>
          <div onClick={() => setShowStatusLabelsConfig(false)} style={{
            position: "fixed", inset: 0, zIndex: 80,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 440, radius: 24, padding: "20px 22px 22px", zIndex: 81 })}>
            <ModalHeader
              t={t} dark={dark} F={F}
              title="Status-Bezeichnungen"
              subtitle="Anzeigenamen für die vier Status. Interne Logik bleibt unverändert."
              onClose={() => setShowStatusLabelsConfig(false)}
              marginBottom={16}
            />

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
              {STATUS_KEYS.map(k => (
                <div key={k} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ minWidth: 80, fontSize: 12, color: t.textMuted, fontFamily: "monospace" }}>{k}</span>
                  <input
                    defaultValue={storeStatusLabels[k] || k}
                    onBlur={e => { if (e.target.value !== (storeStatusLabels[k] || k)) updateLabel(k, e.target.value); }}
                    onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                    style={{ flex: 1, padding: "10px 12px", background: t.inpBg || (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"), border: `1px solid ${t.glassBorder}`, borderRadius: 10, color: t.text, fontFamily: F, fontSize: 14, outline: "none" }}
                  />
                </div>
              ))}
            </div>

            <button onClick={resetDefaults} style={{
              width: "100%", padding: "11px", background: "transparent", border: `1px solid ${t.glassBorder}`,
              borderRadius: 12, color: t.textSub, fontSize: 13, fontWeight: 500, fontFamily: F, cursor: "pointer",
            }}>Auf Standard zurücksetzen</button>
          </div>
        </>
        );
      })()}

      {/* ── MEMBERS MODAL ── */}
      {showMembers && (() => {
        const ROLE_LABELS = { owner: "Inhaber", admin: "Admin", fulfillment: "Fulfillment", finance: "Finanzen", viewer: "Betrachter" };
        const ROLE_OPTIONS_INVITE = [
          { v: "admin",       l: "Admin"       },
          { v: "fulfillment", l: "Fulfillment" },
        ];
        return (
        <>
          <div onClick={() => { setShowMembers(false); setCreatedInviteCode(null); }} style={{
            position: "fixed", inset: 0, zIndex: Z.sheetBackdrop,
            background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
            backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
            animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
            willChange: "opacity",
          }} />
          <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 560, radius: 24, padding: "20px 22px 22px", zIndex: Z.sheet })}>
            <ModalHeader
              t={t} dark={dark} F={F}
              title="Mitglieder"
              subtitle={currentStore?.name || ""}
              onClose={() => { setShowMembers(false); setCreatedInviteCode(null); }}
              marginBottom={16}
            />

            <div style={{ ...glass({ borderRadius: 12, padding: 3 }), display: "flex", marginBottom: 16 }}>
              {[
                { v: "members", l: `Mitglieder · ${membersList.length}` },
                { v: "invites", l: `Einladungen · ${invitationsList.length}` },
              ].map(tab => (
                <button key={tab.v} onClick={() => setMemberSheetTab(tab.v)} style={{
                  flex: 1, padding: "9px 4px", border: "none", cursor: "pointer", fontFamily: F,
                  fontSize: 13, fontWeight: memberSheetTab === tab.v ? 600 : 500, borderRadius: 9,
                  background: memberSheetTab === tab.v ? t.pillActive : "transparent",
                  color: memberSheetTab === tab.v ? t.text : t.textSub,
                  transition: "all 0.2s ease",
                }}>{tab.l}</button>
              ))}
            </div>

            {memberSheetTab === "members" && (
              <div>
                {memberLoading && <div style={{ fontSize: 13, color: t.textMuted, padding: 16, textAlign: "center" }}>Lade…</div>}
                {!memberLoading && membersList.length === 0 && <div style={{ fontSize: 13, color: t.textMuted, padding: 16, textAlign: "center" }}>Keine Mitglieder gefunden</div>}
                <div style={{ ...glass({ borderRadius: 14 }), overflow: "hidden" }}>
                  {membersList.map((m, i) => (
                    <React.Fragment key={m.id}>
                      {i > 0 && <div style={{ height: "0.5px", background: t.divider, marginLeft: 16 }} />}
                      <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 14, color: t.text, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {m.email || m.user_id.slice(0, 8)}
                            {m.user_id === authUser?.id && <span style={{ marginLeft: 6, fontSize: 11, color: t.textMuted, fontWeight: 400 }}>(du)</span>}
                          </div>
                        </div>
                        {currentRole === "owner" && m.user_id !== authUser?.id ? (
                          <select value={m.role} onChange={e => changeMemberRole(m.user_id, e.target.value)} style={{ padding: "5px 8px", fontSize: 12, fontFamily: F, background: t.pillActive, color: t.text, border: `1px solid ${t.glassBorder}`, borderRadius: 8, cursor: "pointer" }}>
                            <option value="owner">Inhaber</option>
                            <option value="admin">Admin</option>
                            <option value="fulfillment">Fulfillment</option>
                          </select>
                        ) : (
                          <span style={{ fontSize: 12, padding: "4px 10px", background: t.pillActive, borderRadius: 9, color: t.textSub, fontWeight: 500 }}>{ROLE_LABELS[m.role] || m.role}</span>
                        )}
                        {currentRole === "owner" && m.user_id !== authUser?.id && (
                          <button onClick={() => setConfirmRemoveUserId(m.user_id)} style={{ background: "none", border: "none", color: "var(--accent-red)", fontFamily: F, fontSize: 12, cursor: "pointer", padding: "4px 8px" }}>Entfernen</button>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {memberSheetTab === "invites" && (
              <div>
                <div style={{ ...glass({ borderRadius: 14 }), padding: 14, marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Neuen Mitglied einladen</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select value={newInviteRole} onChange={e => setNewInviteRole(e.target.value)} style={{ flex: 1, padding: "9px 12px", fontSize: 14, fontFamily: F, background: t.pillActive, color: t.text, border: `1px solid ${t.glassBorder}`, borderRadius: 10, cursor: "pointer" }}>
                      {ROLE_OPTIONS_INVITE.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
                    </select>
                    <button onClick={() => createInvitation(newInviteRole)} style={{ padding: "9px 16px", background: "var(--accent-online)", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: F, cursor: "pointer", boxShadow: "0 2px 10px rgba(55,138,221,0.3)" }}>+ Code erstellen</button>
                  </div>
                  {createdInviteCode && (
                    <div style={{ marginTop: 12, padding: "12px 14px", background: "rgba(48,209,88,0.1)", borderRadius: 10, border: "1px solid rgba(48,209,88,0.25)" }}>
                      <div style={{ fontSize: 12, color: "var(--accent-stand)", fontWeight: 600, marginBottom: 4 }}>Einladungs-Code (7 Tage gültig)</div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                        <code style={{ fontSize: 17, fontFamily: "monospace", color: t.text, fontWeight: 700, letterSpacing: "0.1em" }}>{createdInviteCode}</code>
                        <button onClick={() => { navigator.clipboard?.writeText(createdInviteCode); toast.success("Code kopiert"); }} style={{ padding: "6px 12px", background: t.pillActive, border: `1px solid ${t.glassBorder}`, borderRadius: 8, color: t.text, fontSize: 12, fontWeight: 500, fontFamily: F, cursor: "pointer" }}>Kopieren</button>
                      </div>
                      <div style={{ fontSize: 12, color: t.textMuted, marginTop: 6, lineHeight: 1.4 }}>
                        Sende diesen Code an die Person. Sie kann sich registrieren und unter "Einladungscode eingeben" beitreten.
                      </div>
                    </div>
                  )}
                </div>

                {invitationsList.length > 0 ? (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>Offene Einladungen</div>
                    <div style={{ ...glass({ borderRadius: 14 }), overflow: "hidden" }}>
                      {invitationsList.map((inv, i) => {
                        const expSoon = (new Date(inv.expires_at).getTime() - Date.now()) < 24 * 3600 * 1000;
                        return (
                          <React.Fragment key={inv.id}>
                            {i > 0 && <div style={{ height: "0.5px", background: t.divider, marginLeft: 16 }} />}
                            <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <code style={{ fontSize: 14, fontFamily: "monospace", color: t.text, fontWeight: 700, letterSpacing: "0.06em" }}>{inv.code}</code>
                                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                                  {ROLE_LABELS[inv.role] || inv.role} · läuft ab {new Date(inv.expires_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
                                  {expSoon && <span style={{ color: "var(--accent-orange-strong)", marginLeft: 6 }}>(bald)</span>}
                                </div>
                              </div>
                              <button onClick={() => { navigator.clipboard?.writeText(inv.code); toast.success("Code kopiert"); }} style={{ background: "none", border: "none", color: "var(--accent-online)", fontFamily: F, fontSize: 12, cursor: "pointer", padding: "4px 8px" }}>Kopieren</button>
                              <button onClick={() => revokeInvitation(inv.id)} style={{ background: "none", border: "none", color: "var(--accent-red)", fontFamily: F, fontSize: 12, cursor: "pointer", padding: "4px 8px" }}>Widerrufen</button>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: t.textMuted, textAlign: "center", padding: 16 }}>Keine offenen Einladungen</div>
                )}
              </div>
            )}
          </div>

          {confirmRemoveUserId && (() => {
            const target = membersList.find(m => m.user_id === confirmRemoveUserId);
            return (
              <>
                <div onClick={() => setConfirmRemoveUserId(null)} style={{
                  position: "fixed", inset: 0, zIndex: Z.nestedBackdrop,
                  background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
                  backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
                  animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
                  willChange: "opacity",
                }} />
                <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh: appVh, kbOpen: appKbOpen, offsetTop: appOffsetTop, glass, F, dark, width: 360, radius: 24, zIndex: Z.nestedSheet })}>
                  <ModalHeader
                    t={t} dark={dark} F={F}
                    title="Mitglied entfernen?"
                    subtitle={target?.email || "Dieses Mitglied"}
                    accent={t.accent.red}
                    accentIcon={
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <line x1="22" y1="11" x2="16" y2="11"/>
                      </svg>
                    }
                    onClose={() => setConfirmRemoveUserId(null)}
                  />
                  <button onClick={() => setConfirmRemoveUserId(null)} style={{
                    width: "100%", background: "rgba(55,138,221,0.92)", border: "none", borderRadius: 12,
                    padding: "13px", marginBottom: 8,
                    fontFamily: F, fontWeight: 700, fontSize: 15, cursor: "pointer", color: "#fff",
                    letterSpacing: "-0.1px", boxShadow: "0 4px 20px rgba(55,138,221,0.3)",
                  }}>Abbrechen</button>
                  <button onClick={() => removeMember(confirmRemoveUserId)} style={{
                    width: "100%", background: "transparent", border: "none",
                    padding: "11px", fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer", color: t.accent.red.solid,
                  }}>Entfernen</button>
                </div>
              </>
            );
          })()}
        </>
        );
      })()}

      {/* ── SETTINGS SHEET (v2 Bottom-Sheet) ── */}
      {showSettings && (
       <>
        <div
          data-testid="store-settings-backdrop"
          className="fixed inset-0 z-[2000] bg-black/40 backdrop-blur-[20px]"
          style={{ animation: 'fadeIn 0.2s ease backwards' }}
          onClick={() => setShowSettings(false)}
        />
        <div
          className="fixed inset-x-0 bottom-0 z-[2001] flex max-h-[92vh] flex-col rounded-t-3xl overflow-hidden"
          style={{
            paddingBottom: 'env(safe-area-inset-bottom)',
            animation: 'slideUpSheet 0.3s cubic-bezier(0.32,0.72,0,1) backwards',
            background: dark ? '#2C2C2E' : '#FFFFFF',
          }}
        >
          <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
            <div className="bottom-sheet-handle" />
          </div>
          <div className="flex items-center justify-between px-5 pb-3 flex-shrink-0">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold">Einstellungen</h2>
              {currentStore?.name && (
                <p className="text-[12px] text-muted-foreground truncate">{currentStore.name}</p>
              )}
            </div>
            <button onClick={() => setShowSettings(false)} className="ghost-icon-btn flex-shrink-0" aria-label="Schließen">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="px-4 pb-4">
          {/* === SEKTION 1 — ANZEIGE & WORKFLOW === */}
          {currentRole !== 'fulfillment' && (
            <>
              <SL>Anzeige</SL>
              <SG>
                <STR
                  icon={<UserCheck size={18} />}
                  label="Helfer-Modus"
                  sub={helperMode ? "Aktiv — nur Bereit-Bestellungen sichtbar" : "Eingeschränkte Ansicht für Vertretungen"}
                  checked={!!helperMode}
                  onChange={toggleHelperMode}
                />
              </SG>
            </>
          )}

          {/* === SEKTION 2 — BESTELL-FILTER === */}
          <SL>Bestell-Filter</SL>
          <SG>
            <SVR
              icon={<Filter size={18} />}
              label="Kanal"
              value={filterKanal === "Alle" ? "Alle" : (getAllChannels().find(c => c.id === filterKanal)?.name || filterKanal)}
              onClick={() => setFilterPickerOpen(true)}
            />
            <SD />
            <SSR
              icon={<Clock size={18} />}
              label="Zeitraum"
              sub="Versendet & Retoure"
              options={[{ v: 30, l: "30T" }, { v: 90, l: "90T" }, { v: -1, l: "Alles" }]}
              value={dateRange}
              onChange={setDateRange}
            />
          </SG>

          {/* === SEKTION 3 — DATEN === */}
          <SL>Daten</SL>
          <SG>
            <SVR
              icon={<Archive size={18} />}
              label="Archiv"
              value={String(archive.length)}
              onClick={() => { setShowSettings(false); setTimeout(() => setShowArchive(true), 200); }}
            />
            {can("read") && (
              <>
                <SD />
                <SR
                  icon={<Download size={18} />}
                  label="Als CSV exportieren"
                  onClick={() => { exportCSV(); setShowSettings(false); }}
                />
              </>
            )}
          </SG>

          {/* === SEKTION 4 — STORE (Admin/Owner) === */}
          {currentStore && can("manage_members") && (
            <>
              <SL>Store</SL>
              <SG>
                <div className="px-4 py-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-semibold truncate">{currentStore.name}</div>
                    <div className="text-[11px] text-[var(--label-tertiary)] mt-0.5">
                      {{owner:"Inhaber",admin:"Admin",fulfillment:"Fulfillment",finance:"Finanzen",viewer:"Betrachter"}[currentRole] || currentRole}
                    </div>
                  </div>
                </div>
                <SD />
                {userStores.length > 1 && (
                  <>
                    <SR icon={<ArrowLeftRight size={18} />} label="Store wechseln" onClick={() => { setShowSettings(false); onBack(); }} />
                    <SD />
                  </>
                )}
                <SR icon={<Users size={18} />} label="Mitglieder verwalten" testId="setting-members" onClick={() => { setShowSettings(false); setShowMembers(true); setMemberSheetTab("members"); loadMembersAndInvitations(); }} />
                <SD />
                <SR icon={<ShoppingBag size={18} />} label="Verkaufskanäle" testId="setting-channels" onClick={() => { setShowSettings(false); setShowChannelsConfig(true); }} />
                <SD />
                <SR icon={<Tag size={18} />} label="Status-Bezeichnungen" testId="setting-status-labels" onClick={() => { setShowSettings(false); setShowStatusLabelsConfig(true); }} />
                <SD />
                <SR icon={<History size={18} />} label="Aktivitätsverlauf" onClick={() => { setShowSettings(false); setShowAuditLog(true); setAuditFilter({ entity_type: "all", days: 7 }); setAuditEntries([]); setTimeout(() => loadAuditLog(true), 0); }} />
                {currentRole === "owner" && (
                  <>
                    <SD />
                    <SR icon={<Trash2 size={18} />} label="Store löschen" destructive testId="setting-delete-store" onClick={() => { setShowSettings(false); setConfirmDeleteStoreOpen(true); }} />
                  </>
                )}
              </SG>
            </>
          )}

          {/* === SEKTION 4b — HELFER: BEREICH VERLASSEN (nur fulfillment) === */}
          {currentStore && currentRole === 'fulfillment' && (
            <>
              <SL>Bereich</SL>
              <SG>
                <div className="px-4 py-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-semibold truncate">{currentStore.name}</div>
                    <div className="text-[11px] text-[var(--label-tertiary)] mt-0.5">Du hilfst hier</div>
                  </div>
                </div>
                <SD />
                <SR
                  icon={<DoorOpen size={18} />}
                  label="Bereich verlassen"
                  destructive
                  testId="setting-leave-store"
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Bereich verlassen?',
                      subtitle: `Du verlierst den Zugriff auf "${currentStore.name}".`,
                      confirmLabel: 'Verlassen',
                      destructive: true,
                    });
                    if (!ok) return;
                    setShowSettings(false);
                    await leaveCurrentStore();
                  }}
                />
              </SG>
            </>
          )}

          {/* === SEKTION 5 — KONTO === */}
          {authUser?.email && (
            <>
              <SL>Konto</SL>
              <SG>
                <div className="px-4 py-3">
                  <div className="text-[11px] text-[var(--label-tertiary)]">Angemeldet als</div>
                  <div className="text-[13px] mt-0.5 break-all">{authUser.email}</div>
                </div>
                {supabase && (
                  <>
                    <SD />
                    <SR icon={<LogOut size={18} />} label="Abmelden" destructive onClick={async () => { await supabase.auth.signOut(); setShowSettings(false); }} />
                  </>
                )}
              </SG>
            </>
          )}
        </div>
          </div>
        </div>
       </>
      )}

      {/* ── STORE-DELETE-BESTÄTIGUNG ── */}
      {confirmDeleteStoreOpen && currentStore && (
        <ConfirmDeleteStoreSheet
          dark={dark} t={t}
          storeName={currentStore.name}
          storeType={currentStore.type || "online"}
          onCancel={() => setConfirmDeleteStoreOpen(false)}
          onConfirm={async () => { await destroyCurrentStore(); setConfirmDeleteStoreOpen(false); }}
        />
      )}

      {/* ── KANAL-PICKER (aus Settings → Bestell-Filter) ── */}
      {filterPickerOpen && (
        <KanalPickerModal
          t={t} dark={dark} F={F} glass={glass}
          channels={[{ id: "Alle", name: "Alle" }, ...getAllChannels().map(c => ({ id: c.id, name: `${c.name} · ${orders.filter(o => o.channelId === c.id).length}` }))]}
          value={filterKanal}
          onPick={setFilterKanal}
          onClose={() => setFilterPickerOpen(false)}
        />
      )}
    </>
  );
}
